/** @jsxImportSource @emotion/react */
import { BigNeutral, BigSecondary } from "../../../../forms/elements/buttons";
import LabeledData from "../../../../forms/elements/LabeledData";
import { getResource } from "../../../../language/resources/getResource";
import { Row } from "../../../../layout/flexbox";
import { GuestDetails } from "../../../guestDetails";
import { fullName } from "../../../module";
import { Entitlement } from "../../models";
import { COLUMN_SPACING, ROW_SPACING } from "../../../../forms/styles";
import * as R from 'ramda';
import { endIndex, isBlackoutDate } from "../module";
import { MediumText, SmallText } from "../../../../styles/styles";
import { nameWithSeason } from "../../module";
import ImmutableMap from "../../../../language/ImmutableMap";
import { EntitlementType } from "../../../../entitlementTypes/entitlementType";
import { BoxedErrorInverse, BoxedErrorInverseNoBorder } from "../../../../forms/elements/boxedMessage/boxedMessageStyles";
import { StyledBoxedMessage } from "../../../../forms/elements/boxedMessage/StyledBoxedMessage";
import { useState } from "react";
import { apostrophize } from "../../../../language/strings";
import { FNF_PAGES } from "./module";
import DateInput from "../../../../forms/elements/DateInput";
import ErrorView from "../../../../forms/ErrorView";
import { isNilOrEmpty } from "../../../../language/module";
import { DatePickerDateWithMessage } from "../../../../forms/elements/models";
import { css } from "@emotion/react";
import { css as othercss} from '@emotion/css';


const styles = {
  redeemDaysContainer: css`
    padding: 1px 0; 
    margin-top: 12px; 
    max-width: 800px;
    ${COLUMN_SPACING}
    min-height: 200px;
  `,
  embolden: css`
    ${MediumText};
    font-weight: bold;
  `,
  selectContainer: css`
    display:flex; 
    flex-direction: row; 
    margin-left: 35%; 
    margin-top: -10px;
  `,
  selectNumberItem: css`
    display:flex; 
    flex-direction: column; 
    width: 100px;
  `,
  selectReturnNumberItem: css`
    display:flex; 
    flex-direction: column; 
    width: 100px;
    margin-left: 35%; 
    margin-top: -10px;
  `,
  selectDateItem: css`
    display:flex; 
    flex-direction: column; 
    width: 170px;
    margin-left:10px;
  `,
  selectLabel: css`
    text-align: center;
    ${SmallText}
  `
}

const blackoutCalendarDay = othercss({
  backgroundColor: '#AD4A4A',
  borderRadius: '5px',
  '&:hover': {
    backgroundColor: '#AD4A4A',
  },
})

const redeemConfirmationMessage = (allowCorrectiveRedemption: boolean) => css`
  display: block; 
  ${MediumText}; 
  padding-top: ${allowCorrectiveRedemption ? 0 : 25}px;
`;


type Props = {
  guest: GuestDetails;
  entitlement: Entitlement;
  allowCorrectiveRedemption: boolean;
  allEntitlementTypes: ImmutableMap<EntitlementType> | undefined;
  blackoutDates: Date[];
  datePickerBlackoutDates: DatePickerDateWithMessage[];
  datePickerMinDate: Date | undefined;
  datePickerMaxDate: Date | undefined;
  resources: Record<string, any>;
  dateFormat: string;
  currentPage: FNF_PAGES;
  setCurrentPage: (page: FNF_PAGES) => void;
  redeemEntitlement: (redeemQuantity: number, redeemDate: Date) => void;
  redemptionError: string | undefined;
}

export const FnFRedeemDays = (props: Props) => {
  const [redeemQuantity, setRedeemQuantity] = useState(props.currentPage === FNF_PAGES.RETURN_DAYS ? -1 : 1);
  const [redeemDate, setRedeemDate] = useState<Date | undefined>(
    (props.currentPage === FNF_PAGES.RETURN_DAYS || props.entitlement.isValid) ? new Date() : undefined);

  const generateRedeemOptions = (usedDays: number, unusedDays: number, totalDays: number): React.ReactNode[] => {
    const allOptions = R.range(1, totalDays + 1);
    const positiveOptions = allOptions.slice(0, endIndex(unusedDays, allOptions.length, usedDays));
    return positiveOptions.map(i => (
      <option key={i} value={i}>{i} Day{i === 1 ? '' : 's'}</option>),
    );
  };

  const generateReturnOptions = (usedDays: number): React.ReactNode[] => {
    const negativeOptions = props.allowCorrectiveRedemption ? R.range(-1 * usedDays, 0) : [];
    return negativeOptions.map(i => (
      <option key={i} value={i}>{i} Day{i === 1 || i === -1 ? '' : 's'}</option>),
    );
  };

  const onChangeRedeemDate = (date: Date) => {
    // Datepicker excludes time - set time here since resort tools api only creates new usage 
    // if the redeem date (including) is different otherwise it updates an existing redemption
    const currDate = new Date();
    setRedeemDate(new Date(date.getFullYear(), date.getMonth(), date.getDate(), currDate.getHours(), currDate.getMinutes(), currDate.getMilliseconds()));
  };

  
  const handleSelectQuantity = event => setRedeemQuantity(+event.target.value);
  const possessiveGuestName  = apostrophize(fullName(props.guest));
  const pluralizedDay        = getResource(props.resources, 'FNF_TABLE_PLURALIZED_DAY', redeemQuantity);

  const renderReturnDays = () => {
    return (
      <div css={styles.redeemDaysContainer}>  
        <span>{getResource(props.resources, 'FNF_SELECT_DAYS_TO_RETURN')}</span>
        <div css={styles.selectReturnNumberItem}>
          <span css={styles.selectLabel}>
            {getResource(props.resources, 'FNF_MODAL_NUMBER')}
          </span>
          <select css={css`padding: 10px 0px; margin: 5px 10px;`} onChange={handleSelectQuantity} value={redeemQuantity}>
            {generateReturnOptions(props.entitlement.used)}
          </select>
        </div>
        <span css={redeemConfirmationMessage(props.allowCorrectiveRedemption)}>
          {getResource(props.resources, 'FNF_TABLE_CONFIRMATION_RETURN')}<span css={styles.embolden}>{redeemQuantity}</span>
          {getResource(props.resources, 'FNF_TABLE_CONFIRMATION_NAME', pluralizedDay, possessiveGuestName)}
          <span css={styles.embolden}>
            {
              nameWithSeason(
                props.entitlement,
                props.allEntitlementTypes || ImmutableMap.empty()
              )
            }
          </span>
        </span>
      </div>
   )
  };

  const renderRedeemDays = () => {
    return (
      <div css={styles.redeemDaysContainer}>
        {
          props.entitlement.isValid ?
            <span>{getResource(props.resources, 'FNF_SELECT_DAYS_TO_REDEEM')}</span> :
            <StyledBoxedMessage
                boxStyle={BoxedErrorInverseNoBorder}
                message={getResource(props.resources, 'FNF_TODAY_IS_BLACKOUT_DATE')}
            />
        }
        <div css={styles.selectContainer}>
          <div css={styles.selectNumberItem}>
            <span css={styles.selectLabel}>{getResource(props.resources, 'FNF_MODAL_NUMBER')}</span>
            <select css={css`padding: 12px 0px; margin: 5px 10px;`} onChange={handleSelectQuantity} value={redeemQuantity}>
              {generateRedeemOptions(props.entitlement.used, props.entitlement.available, props.entitlement.maxAllowed)}
            </select>
          </div>
        
          <div css={styles.selectDateItem}>
            <span css={styles.selectLabel}>
              {getResource(props.resources, 'FNF_MODAL_REDEEM_DATE')}
            </span>
            <div css={css`width: 9.4rem; margin: 5px 10px;`}>
              <DateInput
                date={redeemDate}
                onChange={onChangeRedeemDate}
                placeholderText={getResource(props.resources, 'SELECT_DATE_PLACEHOLDER')}
                tooltipText={getResource(props.resources, 'REDEEM_DATE_TOOLTIP')}
                dateFormat={props.dateFormat}
                minDate={props.datePickerMinDate}
                maxDate={props.datePickerMaxDate}
                dayCss={(date) => isBlackoutDate(date, props.blackoutDates) ? blackoutCalendarDay : ''}
                excludeDates={props.datePickerBlackoutDates}
              />
            </div>
          </div>
        </div>

        { 
          !props.allowCorrectiveRedemption &&
          <StyledBoxedMessage
              boxStyle={BoxedErrorInverse}
              message={getResource(props.resources, 'DTW_ENTITLEMENT_MODAL_MESSAGE')}
          />
        }

        <span css={redeemConfirmationMessage(true)}>
          {getResource(props.resources, 'FNF_TABLE_CONFIRMATION_REDEEM')}<span css={styles.embolden}>{redeemQuantity}</span>
          {getResource(props.resources, 'FNF_TABLE_CONFIRMATION_NAME', pluralizedDay, possessiveGuestName)}
          <span css={styles.embolden}>
            {
              nameWithSeason(
                props.entitlement,
                props.allEntitlementTypes || ImmutableMap.empty()
              )
            }
          </span>
        </span>
      </div>
    )
  }

  return (
    <div css={styles.redeemDaysContainer}>
      <LabeledData label={getResource(props.resources, 'FNF_HISTORY_GUEST_LABEL')} data={fullName(props.guest)} />
      <LabeledData label={getResource(props.resources, 'FNF_MODAL_ENTITLEMENT_LABEL')} data={nameWithSeason(props.entitlement, props.allEntitlementTypes || ImmutableMap.empty()) || ''} />

      {
        props.currentPage === FNF_PAGES.RETURN_DAYS &&
        renderReturnDays() 
      }
  
      {
        props.currentPage === FNF_PAGES.REDEEM_DAYS &&
        renderRedeemDays() 
      }

      <ErrorView
        error={
          !isNilOrEmpty(props.redemptionError)
          ? getResource(props.resources, 'FNF_MODAL_FAILED_REDEMPTION', apostrophize(fullName(props.guest)))
          : undefined
        }
      />

      <Row justifyContent='flex-end' rootCss={css`padding-top: 25px; padding-bottom: 1px; ${ROW_SPACING};`}>
        <BigSecondary onClick={() => props.setCurrentPage(FNF_PAGES.ENTITLEMENTS)}>{getResource(props.resources, 'DTW_ENTITLEMENT_MODAL_CANCEL')}</BigSecondary>
        <BigNeutral 
          onClick={() => props.redeemEntitlement(redeemQuantity, redeemDate!)}
          disabled={redeemDate === undefined && props.currentPage === FNF_PAGES.REDEEM_DAYS}>
            {getResource(props.resources, 'DTW_ENTITLEMENT_MODAL_CONFIRM')}
        </BigNeutral>
      </Row>
    </div>
  )
}
